import React, { useEffect, useState, useCallback, JSX } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Radio, RadioChangeEvent, Spin } from 'antd';
import { BusEvent, EBusEvent } from '@/services/bus-event/bus-event.service';
import {
  SolgariApiService,
  EParticipantOperations,
} from '@/services/solgari-api/solgari-api.service';
import { IAgentApi } from '@/services/solgari-api/interfaces/agent.interface';
import { Agent } from '@/components/Agent/Agent';
import { State } from '@/services/state.service';
import { Dynamics } from '@/services/dynamics/dynamics';
import { IPresenceData, SipJs } from '@/services/sip-js.service';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import { getUriFromAgent } from '@/shared/helpers/get-server-from-agent.helper';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import {
  ESolgariCallType,
  EStatusCode,
} from '@/services/dynamics/interfaces/api/phone-call.interface';
import InputSearch from '@/components/InputSearch/InputSearch';
import Checkbox from 'antd/lib/checkbox';
import { useStore } from '@/components/../stores';
import { PresencesSorted } from '@/shared/constants/SortedPresences';
import './AgentList.scss';

export type AgentListMode = 'addToGroupCall' | 'onCall' | 'transfer';

export interface AgentListProps {
  mode: AgentListMode;
  multipleSelect?: boolean;
  setActiveTab?: any;
}

export const AgentList = observer(
  ({ mode, multipleSelect, setActiveTab }: AgentListProps) => {
    const {
      callStore: {
        conferenceExtension,
        agentsOnCall,
        setAgentsOnCall,
        setCallNote,
        callNote,
      },
    } = useStore();
    const [persistedAgents, setPersistedAgents] = useState<IAgentApi[]>([]);
    const [agents, setAgents] = useState<IAgentApi[]>([]);
    const [presences, setPresences] = useState<IPresenceData[]>([]);
    const [selectedAgent, setSelectedAgent] = useState<string>('');
    const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
    const [agentsLoading, setAgentsLoading] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState('');
    const myselfExtension = getExtensionFromUri(SipJs.authData.uri);

    const isTransferAvailable = (): boolean => {
      const presence = presences.find(
        (p) => p.extension === selectedAgent,
      )?.status;

      return presence === EPresenceStatusApi.AVAILABLE;
    };

    const onChange = (event: RadioChangeEvent) => {
      setSelectedAgent(event.target.value);
    };

    const onCheckBoxChange = (event) => {
      if (!event.target.checked) {
        setSelectedAgents(
          selectedAgents.filter((id) => id !== event.target.value),
        );
      } else {
        const newSelectedAgents = [...selectedAgents];
        newSelectedAgents.push(event.target.value);
        setSelectedAgents(newSelectedAgents);
      }
    };

    const renderAgents = (): JSX.Element => {
      let currentAgents: IAgentApi[] = [];
      switch (mode) {
        case 'addToGroupCall':
          currentAgents = agents.filter(
            (a) =>
              !agentsOnCall
                .map((item) => String(item.extensionId))
                .includes(String(a.extensionId)),
          );
          break;
        case 'onCall':
          currentAgents = [...agentsOnCall].sort((a, b) =>
            a.fullName.localeCompare(b.fullName),
          );
          break;
        case 'transfer':
          currentAgents = [...agents].sort((a, b) =>
            a.fullName.localeCompare(b.fullName),
          );
      }

      if (!currentAgents.length) {
        return mode === 'onCall' ? (
          <div className="agent-list__no-found">No participants found</div>
        ) : (
          <div className="agent-list__no-found">No agents found</div>
        );
      }

      const presencesByExtensions = {};
      currentAgents.forEach((a) => {
        const presenceStatus = presences.find(
          (p) => p.extension === String(a.extensionId),
        )?.status;
        presencesByExtensions[a.extensionId] = presenceStatus;
        a.presence = presenceStatus?.toString() as EPresenceStatusApi;
        const isAgentSelected = selectedAgents.includes(String(a.extensionId));
        if (
          isAgentSelected &&
          presenceStatus !== EPresenceStatusApi.AVAILABLE
        ) {
          setSelectedAgents(
            selectedAgents.filter((id) => id !== String(a.extensionId)),
          );
        }
      });

      const agentsJsx = currentAgents
        .filter((a) => !!a.presence)
        .sort((a, b) => {
          const getPresenceOrder = (status: EPresenceStatusApi): number => {
            const index = PresencesSorted.indexOf(status);
            return index === -1 ? Number.MAX_SAFE_INTEGER : index;
          };

          const presenceDiff =
            getPresenceOrder(a.presence) - getPresenceOrder(b.presence);

          return presenceDiff || a.fullName.localeCompare(b.fullName);
        })
        .map((a) => {
          return (
            <Agent
              key={a.extensionId}
              presenceStatus={presencesByExtensions[a.extensionId]}
              extension={String(a.extensionId)}
              userName={a.fullName}
              multipleSelect={mode !== 'transfer'}
              onChange={onCheckBoxChange}
              mode={mode}
              checked={selectedAgents.includes(String(a.extensionId))}
            />
          );
        });

      if (multipleSelect) {
        return (
          <Checkbox.Group value={selectedAgents} className="agent-list__group">
            {agentsJsx}
          </Checkbox.Group>
        );
      } else {
        return (
          <Radio.Group
            value={selectedAgent}
            className="agent-list__group"
            onChange={onChange}
          >
            {agentsJsx}
          </Radio.Group>
        );
      }
    };

    const getAgentsFromApi = async (): Promise<void> => {
      setAgentsLoading(true);

      const allAgents = await SolgariApiService.getListOfAgents();

      const agents = allAgents.filter(
        (a) => String(a.extensionId) !== myselfExtension,
      );
      setPersistedAgents(agents);

      SipJs.makeSubscriptions(agents);

      setAgents(agents);
      setAgentsLoading(false);
    };

    useEffect(() => {
      getAgentsFromApi();

      return () => SipJs.makeUnSubscriptions(persistedAgents);
    }, []);

    useEffect(() => {
      const subScr = SipJs.presences$.subscribe((presences) => {
        setPresences(presences);
      });

      return () => subScr.unsubscribe();
    }, []);

    useEffect(() => {
      let unSub = null;

      if (mode === 'onCall') {
        unSub = subscribeToGetActualParticipants();
      }

      return () => {
        unSub?.();
      };
    }, []);

    useEffect(() => {
      setSelectedAgents([]);
    }, [mode]);

    useEffect(() => {
      const timerID = setTimeout(() => {
        const clearValue = searchValue.trim().toLowerCase();
        const filteredAgents = persistedAgents.filter((a) =>
          `${a.fullName}`.toLowerCase().includes(clearValue),
        );

        if (!filteredAgents.length) {
          setSelectedAgent('');
        }

        if (
          selectedAgent &&
          filteredAgents.length &&
          !filteredAgents.find((a) => String(a.extensionId) === selectedAgent)
        ) {
          setSelectedAgent(String(filteredAgents[0].extensionId));
        }

        setAgents(filteredAgents);
      }, 600);

      return () => {
        clearTimeout(timerID);
      };
    }, [searchValue, persistedAgents, selectedAgent]);

    const subscribeToGetActualParticipants = () => {
      const subBus$ = BusEvent.getBus$().subscribe((value) => {
        if (!value) {
          return;
        }

        const { event } = value;

        switch (event) {
          case EBusEvent.UPD_ACTUAL_PARTICIPANTS: {
            const agentsOnCall = SipJs.getAgentsOnCall().filter(
              (a) => String(a.extensionId) !== myselfExtension,
            );
            setAgentsOnCall(agentsOnCall);
            break;
          }

          default:
            break;
        }
      });

      return function cleanup() {
        subBus$.unsubscribe();
      };
    };

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue?.(e.currentTarget.value);
      },
      [],
    );

    const blindTransfer = () => {
      const session = SipJs.sessions[0];
      const agent = persistedAgents.find(
        (a) => String(a.extensionId) === selectedAgent,
      );
      SipJs.blindTransfer(session.id, agent);

      const { isDialerActive } = State.state$.getValue();

      Dynamics.savePhoneCallNote(
        {
          desc: '',
          entity: session.remoteUser.connectedUsers[0] as IEntity,
          timer: session.timer,
          extendedSubject: `, transferred to Agent ${agent.fullName}`,
          // callState: EStatusCode.MADE,
          callState: EStatusCode.OPEN,
          callType: isDialerActive
            ? ESolgariCallType.DIALER
            : ESolgariCallType.REGULAR,
        },
        session,
      );
    };

    const makeCall = () => {
      const agent = persistedAgents.find(
        (a) => String(a.extensionId) === selectedAgent,
      );
      const entity: IEntity = {
        id: agent.authId,
        fullName: agent.fullName,
        initialPhone: String(agent.extensionId),
        phone: String(agent.extensionId),
        secondPhone: null,
        type: null,
        emails: [],
        phones: [String(agent.extensionId)],
      };
      State.setState({ activeEntities: [entity] });

      const uri = getUriFromAgent(agent);
      SipJs.makeCall(uri, true);
    };

    const createConference = async (agentsToAdd: IAgentApi[]) => {
      const newConferenceId =
        await SolgariApiService.createConference(conferenceExtension);
      const newConference = {
        conferenceId: newConferenceId,
        extensionNumber: conferenceExtension,
        customer: SipJs.sessions[0].remoteUser.connectedUsers[0] as object,
        participants: agentsToAdd,
      };
      SipJs.conference = { ...newConference };
      transferCustomerToConference();

      sendMessagesWithConferenceData(newConference, agentsToAdd);
      const emails = [...agentsToAdd.map((agent) => agent.email)];
      setSelectedAgents([]);
      setAgentsOnCall([...agentsToAdd]);
      setActiveTab('2');
      SolgariApiService.updateConferenceParticipants(
        newConferenceId,
        emails,
        EParticipantOperations.INVITE,
      );
    };

    const transferCustomerToConference = async () => {
      const session = SipJs.sessions[0];
      SipJs.blindTransfer(session.id, conferenceExtension, true);

      const { isDialerActive } = State.state$.getValue();

      const extendedSubject = `, transferred to conference ${conferenceExtension}`;

      const { id } = await Dynamics.savePhoneCallNote(
        {
          desc: '',
          entity: session.remoteUser.connectedUsers[0] as IEntity,
          timer: session.timer,
          extendedSubject,
          callState: EStatusCode.OPEN,
          callType: isDialerActive
            ? ESolgariCallType.DIALER
            : ESolgariCallType.REGULAR,
        },
        session,
      );

      setCallNote({ ...callNote, id, extendedSubject, text: '' });
      State.setState({ callNoteId: id });
    };

    const sendMessagesWithConferenceData = (
      conference,
      agentsToAdd: IAgentApi[],
    ) => {
      const body = {
        event: 'inviteAgent__send_conference_data',
        data: {
          conference: { ...conference },
        },
      };

      agentsToAdd.forEach((a) => {
        const uri = getUriFromAgent(a);
        SipJs.sendMessage(uri, JSON.stringify(body));
      });
    };

    const manageGroupCallAgents = () => {
      const conferenceId = SipJs.conference?.conferenceId;
      const agentsToAdd = [
        ...agents.filter((a) => selectedAgents.includes(String(a.extensionId))),
      ];
      if (!conferenceId) {
        createConference(agentsToAdd);
        return;
      }
      sendMessagesWithConferenceData(SipJs.conference, agentsToAdd);
      setAgentsOnCall([...agentsOnCall, ...agentsToAdd]);
      SolgariApiService.updateConferenceParticipants(
        conferenceId,
        agentsToAdd.map((a) => a.email),
        EParticipantOperations.INVITE,
      );
      setActiveTab('2');
      setSelectedAgents([]);
    };

    let buttonsJsx = <></>;

    switch (mode) {
      case 'addToGroupCall':
        buttonsJsx = (
          <>
            <Button
              disabled={!selectedAgents.length}
              className="agent-list__btn"
              data-testid="invite-agent"
              type="primary"
              onClick={manageGroupCallAgents}
            >
              Invite
            </Button>
          </>
        );
        break;
      case 'transfer':
        buttonsJsx = (
          <>
            <Button
              disabled={!selectedAgent || !isTransferAvailable()}
              className="agent-list__btn answer--black"
              data-testid="call-first"
              onClick={() => makeCall()}
            >
              Call First
            </Button>
            <Button
              disabled={!selectedAgent || !isTransferAvailable()}
              className="agent-list__btn"
              data-testid="blind-transfer"
              type="primary"
              onClick={() => blindTransfer()}
            >
              Blind Transfer
            </Button>
          </>
        );
    }

    return (
      <div className="agent-list">
        {mode !== 'onCall' && (
          <InputSearch
            className="agent-list__search"
            data-testid="agent-list-search"
            onChange={handleSearchChange}
            placeholder="Search"
          />
        )}
        <div className="agent-list__agents">
          {agentsLoading ? (
            <Spin className="agent-list__spinner" />
          ) : (
            renderAgents()
          )}
        </div>
        <div className="agent-list__controls">{buttonsJsx}</div>
      </div>
    );
  },
);
